import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from '../../hooks/use-toast';
import { Upload, CheckCircle2, Clock, ArrowLeft } from 'lucide-react';
import { BrandLogo } from '../BrandLogo';

const DriverOnboarding = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [step, setStep] = useState(user?.verified ? 'verified' : 'form');
  const [formData, setFormData] = useState({
    fullName: user?.name || '',
    aadharNumber: '',
    drivingLicense: '',
    experience: '',
    address: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validation
    if (!formData.fullName.trim()) {
      toast({
        title: 'Enter Full Name',
        description: 'Please enter your full name',
        variant: 'destructive'
      });
      return;
    }

    if (!formData.aadharNumber || formData.aadharNumber.length !== 12) {
      toast({
        title: 'Invalid Aadhaar',
        description: 'Please enter a valid 12-digit Aadhaar number',
        variant: 'destructive'
      });
      return;
    }

    if (!formData.drivingLicense.trim()) {
      toast({
        title: 'Enter License',
        description: 'Please enter your driving license number',
        variant: 'destructive'
      });
      return;
    }

    if (!formData.experience) {
      toast({
        title: 'Enter Experience',
        description: 'Please enter your driving experience',
        variant: 'destructive'
      });
      return;
    }

    // Mock submission — update user with pending verification status
    const updatedProfile = {
      ...user,
      ...formData,
      name: formData.fullName,
      verified: false,
      onboardingSubmitted: true
    };
    
    updateUser(updatedProfile);
    
    // Save to driverProfile for persistence
    localStorage.setItem('driverProfile', JSON.stringify(updatedProfile));

    setStep('pending');

    toast({
      title: 'Application Submitted!',
      description: 'Your details are under verification. You will be notified once approved.',
      duration: 5000
    });
  };

  if (step === 'verified') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-12 h-12 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold mb-2">Account Verified!</h2>
              <p className="text-muted-foreground mb-6">
                Your account has been successfully verified. You can now start accepting bookings.
              </p>
              <Button
                className="w-full bg-primary hover:bg-primary-dark"
                onClick={() => navigate('/driver/dashboard')}
              >
                Go to Dashboard
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (step === 'pending') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-12 h-12 text-yellow-600" />
              </div>
              <h2 className="text-2xl font-bold mb-2">Verification Pending</h2>
              <p className="text-muted-foreground mb-4">
                Your application is under review. We are verifying your documents.
              </p>
              <div className="bg-accent/10 border border-accent/30 rounded-lg p-4 mb-6">
                <p className="text-sm text-primary">
                  This usually takes 24-48 hours. You will receive a notification once verified.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-white border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/driver/dashboard')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <BrandLogo variant="compact" className="h-8 w-auto" />
              <span className="text-sm font-semibold text-muted-foreground border-l pl-3">Onboarding</span>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 max-w-2xl">
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">Driver Onboarding</CardTitle>
            <CardDescription>
              Complete your profile to start accepting ride bookings. We'll verify your details for safety.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Personal Details */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg">Personal Details</h3>
                
                <div>
                  <Label htmlFor="fullName">Full Name *</Label>
                  <Input
                    id="fullName"
                    name="fullName"
                    placeholder="Enter your full name"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    className="h-12"
                  />
                </div>

                <div>
                  <Label htmlFor="aadharNumber">Aadhaar Number *</Label>
                  <Input
                    id="aadharNumber"
                    name="aadharNumber"
                    type="text"
                    placeholder="Enter 12-digit Aadhaar number"
                    value={formData.aadharNumber}
                    onChange={handleInputChange}
                    maxLength={12}
                    className="h-12"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Your Aadhaar will be verified for identity confirmation
                  </p>
                </div>

                <div>
                  <Label htmlFor="address">Address *</Label>
                  <Input
                    id="address"
                    name="address"
                    placeholder="Enter your full address"
                    value={formData.address}
                    onChange={handleInputChange}
                    className="h-12"
                  />
                </div>
              </div>

              {/* Driving Details */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg">Driving Details</h3>
                
                <div>
                  <Label htmlFor="drivingLicense">Driving License Number *</Label>
                  <Input
                    id="drivingLicense"
                    name="drivingLicense"
                    placeholder="e.g., DL1420210012345"
                    value={formData.drivingLicense}
                    onChange={handleInputChange}
                    className="h-12"
                  />
                </div>

                <div>
                  <Label htmlFor="experience">Driving Experience (in years) *</Label>
                  <Input
                    id="experience"
                    name="experience"
                    type="number"
                    placeholder="e.g., 5"
                    value={formData.experience}
                    onChange={handleInputChange}
                    min="1"
                    max="50"
                    className="h-12"
                  />
                </div>
              </div>

              {/* Document Upload Info */}
              <div className="bg-accent/10 border border-accent/30 rounded-lg p-4">
                <div className="flex gap-3">
                  <Upload className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-primary mb-1">Document Verification</h4>
                    <p className="text-sm text-primary">
                      For now, we're collecting your details. In production, you'll be able to upload 
                      scanned copies of your Aadhaar and Driving License for verification.
                    </p>
                  </div>
                </div>
              </div>

              {/* Terms */}
              <div className="bg-background border rounded-lg p-4">
                <p className="text-sm text-foreground">
                  By submitting this form, you agree to our terms of service and confirm that all 
                  information provided is accurate. False information may result in account suspension.
                </p>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                size="lg"
                className="w-full bg-primary hover:bg-primary-dark text-lg"
              >
                Submit for Verification
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DriverOnboarding;
